import { useOrganizationDetail } from "@/api/services/admin/organization";
import { useProductList } from "@/api/services/admin/product";
import { useProductContractsByOrganization } from "@/api/services/admin/product-contract";
import { TProductListItem } from "@/api/services/admin/product/dtos/get-product-list.dto";
import { IconErrorOutline } from "@/assets/icons";
import { Checkbox } from "@/components/elements/Checkbox";
import { EllipsisText } from "@/components/elements/EllipsisText";
import { SectionCard } from "@/components/elements/SectionCard";
import { SegmentedControl } from "@/components/elements/SegmentedControl/SegmentedControl";
import { Table, TableSorting } from "@/components/elements/Table";
import { DATE_FORMAT } from "@/constants/datetime";
import { EOrganizationStatus } from "@/constants/enum";
import { EMPTY_STR } from "@/constants/string";
import { ContractServiceBadge } from "@/features/organization/components/ContractServiceBadge";
import { CONTRACT_FILTER_KEYS, CONTRACT_FILTER_OPTIONS } from "@/features/organization/constants/organizationDetail";
import { useSortedProducts } from "@/features/organization/hooks/useSortedProducts";
import { getContractPlanLabel } from "@/features/organization/utils/getContractPlanLabel";
import { useTable } from "@/hooks/useTable";
import { black, state, text } from "@/theme/colors";
import { EContractStatus } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { isNumber } from "@/utils/number";
import { Stack, Typography } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { FC, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

type Props = {
  selectedProductIds: number[];
  onToggleProduct: (productId: number) => void;
};

export const OrganizationEditProductSelect: FC<Props> = ({ selectedProductIds, onToggleProduct }) => {
  const { id } = useParams();
  const { data: productData, isLoading: isProductLoading } = useProductList();
  const { data: contractData, isLoading: isContractLoading, isFetching: isContractFetching } = useProductContractsByOrganization(id);
  const { data: organizationData } = useOrganizationDetail();
  const isDeleted = organizationData?.data?.status === EOrganizationStatus.DELETED;
  const productContracts = contractData?.data;

  const contractMap = useMemo(() => new Map(productContracts?.map((c) => [c.product?.code, c]) ?? []), [productContracts]);

  const products = useMemo(
    () =>
      productData?.data?.map((item) => {
        const contract = contractMap.get(item.code);
        if (!contract) return item;
        return {
          ...item,
          productContract: {
            id: contract?.id,
            isTemporary: contract?.isTemporary,
            plan: contract?.plan,
            referenceProductId: contract?.referenceProductId,
            lastLoginAt: contract?.lastLoginAt,
            createdAt: contract?.createdAt,
            status: contract?.status || null,
            activeUserCount: contract?.activeUserCount,
          },
        };
      }) ?? [],
    [productData, contractMap],
  );

  const visibleProducts = useMemo(() => (isDeleted ? products.filter((item) => item.productContract) : products), [products, isDeleted]);

  const [contractFilter, setContractFilter] = useState(CONTRACT_FILTER_KEYS.ALL);

  const filteredProducts = useMemo(() => {
    if (!contractFilter) return visibleProducts;
    switch (contractFilter) {
      case CONTRACT_FILTER_KEYS.DURING_CONTRACT_AND_TRIAL: {
        return visibleProducts.filter(
          (item) =>
            item?.productContract &&
            (item.productContract.isTemporary ||
              item.productContract.status === EContractStatus.DURING_CONTRACT ||
              item.productContract.status === EContractStatus.TRIAL),
        );
      }
      case CONTRACT_FILTER_KEYS.CANCEL: {
        return visibleProducts.filter(
          (item) =>
            item.productContract &&
            (item.productContract.isTemporary ||
              item.productContract.status === EContractStatus.TRIAL_CANCELLED ||
              item.productContract.status === EContractStatus.CANCELED_CONTRACT),
        );
      }
      default: {
        return visibleProducts;
      }
    }
  }, [visibleProducts, contractFilter]);

  const sortedProducts = useSortedProducts(filteredProducts);

  const isRowSelectable = (item: TProductListItem) => !isDeleted && !(item.productContract && !item.productContract.isTemporary);

  const columns: MRT_ColumnDef<TProductListItem>[] = useMemo(
    () => [
      {
        id: "select",
        header: "",
        size: 50,
        Cell: ({ row }) => (
          <Checkbox
            disabled={!isRowSelectable(row.original)}
            checked={selectedProductIds.includes(row.original.id)}
            onChange={() => onToggleProduct(row.original.id)}
            sx={{ p: 0 }}
          />
        ),
      },
      {
        id: "code",
        header: "",
        size: 60,
        Cell: ({ row }) => (
          <ContractServiceBadge products={[{ code: row.original.code, singleCharLabel: row.original.singleCharLabel, name: row.original.name }]} />
        ),
      },
      {
        accessorKey: "name",
        header: "プロダクト名",
        size: 176,
        Header: () => (
          <TableSorting name="productName">
            <Typography variant="body14Bold" color={text.primary}>
              プロダクト名
            </Typography>
          </TableSorting>
        ),
        Cell: ({ row }) => {
          const isActiveContract =
            !row.original?.productContract ||
            row.original?.productContract?.status === null ||
            row.original?.productContract?.status === EContractStatus.DURING_CONTRACT ||
            row.original?.productContract?.status === EContractStatus.TRIAL;
          return (
            <EllipsisText
              title={row.original?.name ?? ""}
              variant="body14"
              maxWidth={150}
              sx={{ textDecoration: isActiveContract ? undefined : "line-through" }}
            />
          );
        },
      },
      {
        id: "plan",
        header: "システム情報",
        size: 176,
        Cell: ({ row }) => {
          if (row.original?.productContract?.isTemporary) {
            return (
              <Stack direction="row" alignItems="center" gap={0.5} sx={{ color: state.error_3 }}>
                <IconErrorOutline fontSize="24px" />
                <Typography variant="body14" color={black[30]}>
                  仮登録
                </Typography>
              </Stack>
            );
          }
          if (!row.original?.productContract) return EMPTY_STR.TEXT;
          const { status, plan } = row.original.productContract;
          return getContractPlanLabel(status || null, plan || "");
        },
      },
      {
        id: "activeUserCount",
        header: "有効ユーザー数",
        size: 130,
        Cell: ({ row }) => (
          <Typography variant="body14">
            {isNumber(row.original?.productContract?.activeUserCount) ? row.original.productContract.activeUserCount : EMPTY_STR.TEXT}
          </Typography>
        ),
      },
      {
        id: "lastLoginAt",
        header: "最終ログイン",
        size: 148,
        Header: () => (
          <TableSorting name="lastLoginAt">
            <Typography variant="body14Bold" color={text.primary}>
              最終ログイン
            </Typography>
          </TableSorting>
        ),
        Cell: ({ row }) =>
          row.original?.productContract?.lastLoginAt ? dayjs(row.original.productContract.lastLoginAt).format(DATE_FORMAT) : EMPTY_STR.DATE,
      },
      {
        id: "createdAt",
        header: "登録日",
        size: 108,
        Header: () => (
          <TableSorting name="createdAt">
            <Typography variant="body14Bold" color={text.primary}>
              登録日
            </Typography>
          </TableSorting>
        ),
        Cell: ({ row }) =>
          row.original?.productContract?.createdAt ? dayjs(row.original.productContract.createdAt).format(DATE_FORMAT) : EMPTY_STR.DATE,
      },
    ],
    [selectedProductIds, onToggleProduct, isDeleted],
  );

  const { table } = useTable({
    columns,
    rows: sortedProducts,
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor: row.original?.productContract?.isTemporary ? `${state.warning_3} !important` : undefined,
      },
    }),
  });

  return (
    <SectionCard
      title={`契約対象 (${selectedProductIds.length})`}
      titleVariant="head20Bold"
      maxWidth="100%"
      titleColor={black[30]}
      headerAction={<SegmentedControl value={contractFilter} options={CONTRACT_FILTER_OPTIONS} onChange={setContractFilter} />}
    >
      <Stack gap={2}>
        <Table table={table} isLoading={isProductLoading || isContractLoading} isFetching={isContractFetching} />
        <Stack>
          <Typography variant="body14" color={text.secondary}>
            お客様が契約するプロダクトをチェックしてください。
          </Typography>
          <Typography variant="body14" color={text.secondary}>
            新規契約時は仮登録されるため、保存後にプロダクトを選択して登録を進めてください。
          </Typography>
        </Stack>
      </Stack>
    </SectionCard>
  );
};
